/**
 * Cycle deadlines — compute the next relevant application-cycle
 * deadline for a given year-of-entry.
 *
 * We cover the three deadlines students care about:
 *   • UCAS main equal consideration (29 January, year-of-entry)
 *   • Common App Early Action main (1 November, year-of-entry − 1)
 *   • Common App Regular Decision (1 January, year-of-entry)
 *
 * The ticket shows the single soonest upcoming deadline, favouring
 * UCAS (the biggest audience) when two land on the same day.
 */

const UCAS_LABEL = "UCAS equal consideration";
const CA_EA_LABEL = "Common App Early Action";
const CA_RD_LABEL = "Common App Regular Decision";


/**
 * Build the three candidate deadlines for `year`. Returns an array of
 * `{name, date}` entries. If `year` isn't supplied we fall back to
 * next year (if we're already past Sept) or this year.
 */
function _candidates(year) {
  const y = Number(year) || _inferYearOfEntry();
  return [
    { name: CA_EA_LABEL, date: new Date(y - 1, 10, 1) },  // 1 Nov (Y-1)
    { name: CA_RD_LABEL, date: new Date(y,     0, 1) },   // 1 Jan Y
    { name: UCAS_LABEL,  date: new Date(y,     0, 29) },  // 29 Jan Y
  ];
}

function _inferYearOfEntry() {
  const now = new Date();
  // Past 1 September → the "next" entry year is next year.
  return now.getMonth() >= 8 ? now.getFullYear() + 1 : now.getFullYear();
}


export function nextDeadlineForYear(year) {
  const now = new Date();
  const upcoming = _candidates(year)
    .filter((d) => d.date.getTime() > now.getTime())
    .sort((a, b) => a.date - b.date);
  if (upcoming.length === 0) return null;
  // Prefer UCAS when two deadlines are the soonest on the same day
  // (never happens in practice, but keeps the picker deterministic).
  const soonestMs = upcoming[0].date.getTime();
  const sameDay = upcoming.filter((d) => d.date.getTime() === soonestMs);
  return sameDay.find((d) => d.name === UCAS_LABEL) || sameDay[0];
}


export function cycleLabelForYear(year) {
  const y = Number(year);
  if (!y) return null;
  return `UCAS ${y}`;
}


/**
 * Whole-day difference between two Dates. Rounded up so a deadline
 * later today still shows "1 day" rather than "0".
 */
export function daysBetween(from, to) {
  const ms = to.getTime() - from.getTime();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}
